/**
 * SecurityCompliancePage — Wizard pós-login para completar requisitos de segurança pendentes.
 *
 * Exibida quando o login retorna `pendingSecuritySteps` não vazio ou requer aprovação de dispositivo.
 * O usuário está temporariamente autenticado (tem JWT provisório).
 *
 * Steps suportados:
 * - 'device_approval' → Aguardar aprovação do login via dispositivo móvel (mestre)
 * - 'passkey' → Cadastrar chave de acesso (WebAuthn) local/biométrica
 * - 'email_verification' → Verificar email via código
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Fingerprint, Mail, CheckCircle2, ArrowRight, Loader2, AlertTriangle, Smartphone } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { usePasskeyEnrollment } from '../../hooks/usePasskeyEnrollment';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import { DeviceApprovalWait } from '../../components/security/DeviceApprovalWait';
import { api } from '../../api/client';
import { clearPasskeyOnThisDevice, isPasskeyAvailableOnThisDevice, markPasskeyOnThisDevice } from '../../auth/passkeyDevice';
import { getRedirectPath } from '../../auth/routes';
import { saveAccountToLocal } from '../../auth/savedAccounts';
import { registerApprovalDevice } from '../../security/deviceApprovalApi';
import { getPasskeyStatus } from '../../security/passkey/passkeyApi';

/** Metadata de cada step */
const STEP_META: Record<string, { title: string; description: string; icon: any }> = {
  device_approval: {
    title: 'Aprovação de Dispositivo',
    description: 'Aguarde a liberação deste computador no seu celular principal.',
    icon: Smartphone,
  },
  passkey: {
    title: 'Chave de Acesso',
    description: 'Configure sua chave de acesso biométrica local para logins futuros.',
    icon: Fingerprint,
  },
  email_verification: {
    title: 'Verificar E-mail',
    description: 'Confirme seu endereço de e-mail com o código enviado.',
    icon: Mail,
  },
};

function readStoredAuthUser(): any | null {
  try {
    const raw = localStorage.getItem('ac_user') || sessionStorage.getItem('ac_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function SecurityCompliancePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches || 
    (window.navigator as any).standalone === true
  );
  const token = sessionStorage.getItem('ac_token') || localStorage.getItem('ac_token') || '';

  // Redirecionamento PWA para a rota central /install
  useEffect(() => {
    let pendingSteps: string[] = [];
    try {
      const storedPending = sessionStorage.getItem('ac_pending_security');
      pendingSteps = storedPending ? JSON.parse(storedPending) as string[] : [];
    } catch {
      pendingSteps = [];
    }
    const isWaitingDeviceApproval = pendingSteps.includes('device_approval');
    const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    if (isMobile && !isStandalone && !skippedPwaInstall && !isIOSDevice) {
      const forceBrowserLocal = sessionStorage.getItem('ac_force_browser') === 'true' || 
                                new URLSearchParams(window.location.search).get('browser') === '1';
      if (token && !isWaitingDeviceApproval && !forceBrowserLocal) {
        navigate(`/install?redirect=${encodeURIComponent(window.location.pathname)}`, { replace: true });
      }
    }
  }, [isMobile, isStandalone, navigate, token]);

  const [steps, setSteps] = useState<string[]>([]);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Dados para aprovação de dispositivo
  const [approvalData, setApprovalData] = useState<any>(null);

  // Hook de Passkey
  const { registerPasskey, authenticatePasskey, loading: passkeyLoading, error: passkeyError } = usePasskeyEnrollment();

  // Estados de verificação e status da passkey
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);
  const [checkingPasskey, setCheckingPasskey] = useState(false);
  const hasAutoTriggeredRegisterRef = useRef(false);

  // Carregar steps pendentes do sessionStorage
  useEffect(() => {
    try {
      const storedPending = sessionStorage.getItem('ac_pending_security');
      const approvalDataStr = sessionStorage.getItem('ac_device_approval_data');

      let parsedSteps: string[] = [];
      if (storedPending) {
        parsedSteps = JSON.parse(storedPending) as string[];
      }

      if (approvalDataStr) {
        const parsedData = JSON.parse(approvalDataStr);
        setApprovalData(parsedData);
        // Garantir que device_approval está na lista de steps na primeira posição (Desativado temporariamente a pedido do usuário)
        // if (!parsedSteps.includes('device_approval')) {
        //   parsedSteps = ['device_approval', ...parsedSteps];
        // }
      }

      if (parsedSteps.length > 0) {
        setSteps(parsedSteps);
        return;
      }
    } catch (e) {
      console.warn('[SecurityCompliance] Erro ao analisar dados da sessão:', e);
    }

    // Se não tem steps, redirecionar para dashboard ou login
    const savedUser = useAuthStore.getState().user || readStoredAuthUser();
    if (savedUser) {
      navigate(getRedirectPath(savedUser.perfil || '', savedUser.cargo || undefined), { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const currentStep = steps[currentStepIdx];

  // Verificar se o usuário já possui passkeys cadastradas no servidor
  useEffect(() => {
    if (currentStep === 'passkey' && token && hasPasskey === null && !checkingPasskey) {
      const checkPasskeyStatus = async () => {
        setCheckingPasskey(true);
        try {
          const data = await getPasskeyStatus({ bearerToken: token });
          const nextHasPasskey = !!data.temPasskey;
          if (!nextHasPasskey) {
            const targetEmail = (user?.email || approvalData?.pendingUserData?.email || '').trim().toLowerCase();
            if (targetEmail) {
              clearPasskeyOnThisDevice(targetEmail);
            }
          }
          setHasPasskey(nextHasPasskey);
        } catch (e) {
          console.warn('[SecurityCompliance] Erro ao verificar status da passkey:', e);
          setHasPasskey(false);
        } finally {
          setCheckingPasskey(false);
        }
      };
      checkPasskeyStatus();
    }
  }, [currentStep, token, hasPasskey, checkingPasskey]);

  // Se for celular e o step for passkey, mas não há chave física neste dispositivo, redireciona para onboarding de segurança
  useEffect(() => {
    const targetEmail = (user?.email || approvalData?.pendingUserData?.email || '').trim().toLowerCase();
    if (currentStep === 'passkey' && isMobile && targetEmail) {
      const hasLocalKey = isPasskeyAvailableOnThisDevice(targetEmail);
      if (!hasLocalKey) {
        console.log('[SecurityCompliance] Celular sem biometria local detectado. Redirecionando para onboarding de segurança...');
        navigate('/onboarding/security', { replace: true });
      }
    }
  }, [currentStep, isMobile, user, approvalData, navigate]);

  // ── Passkey Authentication (Login/Verificação Biométrica) ──────────
  const handlePasskeyAuthenticate = async () => {
    setLoading(true);
    setError('');
    try {
      const ok = await authenticatePasskey(token);
      if (ok) {
        setSuccess(true);
        sessionStorage.setItem('ac_accesskey_verified', 'true');
        sessionStorage.setItem('ac_pwa_unlocked_this_session', 'true');
        // Registrar dispositivo como privado
        await autoRegisterDevice(true);
        const emailForDevice = (user?.email || approvalData?.pendingUserData?.email || '').trim().toLowerCase();
        if (emailForDevice) {
          markPasskeyOnThisDevice(emailForDevice);
        }
        setTimeout(advanceOrFinish, 1500);
      } else {
        throw new Error('Falha na verificação biométrica local.');
      }
    } catch (err: any) {
      setError(passkeyError || err.message || 'Erro ao verificar chave biométrica.');
    } finally {
      setLoading(false);
    }
  };

  // Disparar autenticação ou cadastro biométrico nativo automaticamente ao carregar
  useEffect(() => {
    if (currentStep === 'passkey' && !success && !loading && !passkeyLoading) {
      if (hasPasskey === true) {
        handlePasskeyAuthenticate();
      } else if (hasPasskey === false && !hasAutoTriggeredRegisterRef.current) {
        hasAutoTriggeredRegisterRef.current = true;
        // Pequeno delay para garantir o carregamento visual da página antes do prompt nativo
        setTimeout(() => {
          handlePasskeyRegister();
        }, 800);
      }
    }
  }, [currentStep, hasPasskey, success]);
  const meta = currentStep ? STEP_META[currentStep] : null;

  // Registrar dispositivo automaticamente no backend (marcando como público/privado)
  const autoRegisterDevice = async (somenteMeu: boolean) => {
    try {
      if (!token) return;
      const ua = navigator.userAgent;
      const navegador = /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Outro';
      const sistemaOp = /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Outro';
      const nome = somenteMeu
        ? (sistemaOp + ' — ' + new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }))
        : ('Público — ' + navegador);
      await registerApprovalDevice(
        {
          fingerprint: getDeviceFingerprint(),
          nome,
          tipoLocal: 'CASA',
          somenteMeu,
          navegador,
          sistemaOp,
        },
        { bearerToken: token },
      );
    } catch (e) {
      console.warn('[SecurityCompliance] Falha ao registrar dispositivo:', e);
    }
  };

  // Concluir login e redirecionar para o dashboard correspondente
  const completeLoginAndRedirect = useCallback(async (userData: any) => {
    const tk = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
    if (tk && userData) {
      const normUserData = {
        ...userData,
        role: userData.perfil,
        roles: [userData.perfil],
        acessoDireto: userData.acessoDireto ?? false,
        statusConta: userData.statusConta,
      };
      saveAccountToLocal(normUserData, 'email');
      useAuthStore.getState().login(tk, normUserData);
      sessionStorage.removeItem('ac_pending_security');
      sessionStorage.removeItem('ac_device_approval_data');
      await useAuthStore.getState().checkAuth();
      const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
      const dashRoute = redirectPath || getRedirectPath(normUserData.perfil || '', normUserData.cargo || undefined);
      sessionStorage.removeItem('ac_redirect_after_login');
      navigate(dashRoute, { replace: true });
    }
  }, [navigate]);

  const advanceOrFinish = useCallback(() => {
    if (currentStepIdx + 1 < steps.length) {
      setCurrentStepIdx(prev => prev + 1);
      setError('');
      setSuccess(false);
      setEmailSent(false);
      setEmailCode('');
    } else {
      // Todos os steps concluídos
      const finalUser = user || approvalData?.pendingUserData || readStoredAuthUser();
      if (finalUser) {
        completeLoginAndRedirect(finalUser);
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, [currentStepIdx, steps, user, approvalData, navigate, completeLoginAndRedirect]);

  // ── Passkey Registration (Chave de Acesso) ──────────────────────────
  const handlePasskeyRegister = async () => {
    setLoading(true);
    setError('');
    try {
      const ua = navigator.userAgent;
      const navegador = /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Outro';
      const sistemaOp = /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Outro';
      const fingerprint = getDeviceFingerprint();
      const nomeDispositivo = sistemaOp + ' — ' + new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

      const ok = await registerPasskey(token, {
        navegador,
        sistemaOp,
        fingerprint,
        nomeDispositivo,
      });

      if (ok) {
        setSuccess(true);
        sessionStorage.setItem('ac_accesskey_verified', 'true');
        sessionStorage.setItem('ac_pwa_unlocked_this_session', 'true');
        // Registrar dispositivo como privado
        await autoRegisterDevice(true);
        let emailForDevice = (user?.email || approvalData?.pendingUserData?.email || '').trim().toLowerCase();
        
        // Fallback robusto via ac_user no localStorage
        if (!emailForDevice) {
          try {
            const storedUser = localStorage.getItem('ac_user') || sessionStorage.getItem('ac_user');
            if (storedUser) {
              const parsedUser = JSON.parse(storedUser);
              emailForDevice = (parsedUser.email || '').trim().toLowerCase();
            }
          } catch (e) {
            console.warn('[SecurityCompliance] Erro ao recuperar fallback de email do localstorage:', e);
          }
        }

        if (emailForDevice) {
          markPasskeyOnThisDevice(emailForDevice);
        }
        setTimeout(advanceOrFinish, 1500);
      } else {
        throw new Error('Falha no registro da biometria local.');
      }
    } catch (err: any) {
      setError(passkeyError || err.message || 'Erro ao registrar chave de acesso.');
    } finally {
      setLoading(false);
    }
  };

  // Pular Passkey no Desktop (computador de uso público)
  const handlePasskeySkip = async () => {
    setLoading(true);
    try {
      // Registrar dispositivo como público
      await autoRegisterDevice(false);
      // Salvar flag local no desktop/mobile para evitar loop de redirecionamento no ProtectedRoute
      if (!isMobile) {
        localStorage.setItem('ac_security_onboarding_skipped_device', '1');
      } else {
        sessionStorage.setItem('ac_security_onboarding_skipped', '1');
      }
      advanceOrFinish();
    } catch (e: any) {
      setError(e.message || 'Erro ao registrar dispositivo público.');
    } finally {
      setLoading(false);
    }
  };

  // ── Email Verification ────────────────────────────────────────────
  const handleSendEmailCode = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/send-email-code');
      setEmailSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao enviar código.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!emailCode.trim()) { setError('Digite o código.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/verify-email-code', { code: emailCode.trim() });
      setSuccess(true)
      setTimeout(advanceOrFinish, 1500);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Código inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  // ── Skip (pular step — para testes/dev) ───────────────────────────
  const handleSkip = () => {
    advanceOrFinish();
  };

  // Se for celular e não for standalone (não PWA instalado), exige instalar o PWA primeiro (exceto iOS ou modo browser)
  if (isMobile && !isStandalone) {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    const forceBrowserLocal = sessionStorage.getItem('ac_force_browser') === 'true' || 
                              new URLSearchParams(window.location.search).get('browser') === '1';
    if (token && currentStep !== 'device_approval' && !forceBrowserLocal && !isIOSDevice) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(-45deg, #0f0a1a, #1a1035, #0d1b2a, #151025)',
          fontFamily: 'Inter, system-ui, sans-serif', padding: 16,
        }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              width: 40, height: 40, border: '3px solid rgba(168, 85, 247, 0.1)',
              borderTopColor: '#c084fc', borderRadius: '50%',
              margin: '0 auto 16px', animation: 'spin .8s linear infinite',
            }} />
            <p style={{ color: '#ffffff', fontSize: '0.9rem' }}>Redirecionando para a instalação do PWA...</p>
          </div>
        </div>
      );
    }
  }

  if (!currentStep || !meta) return null;

  const StepIcon = meta.icon;
  const isLast = currentStepIdx + 1 >= steps.length;
  const completedCount = currentStepIdx + (success ? 1 : 0);
  const envIsDev = import.meta.env.MODE === 'development';

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(-45deg, #0f0a1a, #1a1035, #0d1b2a, #151025)',
      fontFamily: 'Inter, system-ui, sans-serif', padding: 16,
    }}>
      <style>{`
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 20px rgba(124, 58, 237, 0.3); } 50% { box-shadow: 0 0 40px rgba(124, 58, 237, 0.6); } }
      `}</style>
      
      {currentStep === 'device_approval' && !success && approvalData ? (
        <DeviceApprovalWait
          solicitacaoId={approvalData.solicitacaoId}
          perfil={approvalData.pendingUserData?.perfil}
          cargo={approvalData.pendingUserData?.cargo}
          accountName={approvalData.pendingUserData?.nome || approvalData.pendingUserData?.name}
          accountEmail={approvalData.pendingUserData?.email}
          dezenaCorreta={approvalData.dezenaCorreta}
          error={error}
          onApproved={async (opcao) => {
            if (approvalData.pendingMagicToken) {
              setLoading(true);
              setError('');
              try {
                const { data } = await api.post('/auth/magic-link/validate', { token: approvalData.pendingMagicToken });
                if (data.success) {
                  sessionStorage.setItem('ac_provisional_token', data.tempToken);
                  // Limpa e direciona para redefinição
                  sessionStorage.removeItem('ac_pending_security');
                  sessionStorage.removeItem('ac_device_approval_data');
                  navigate('/login?action=recover-reset', { replace: true });
                }
              } catch (err: any) {
                setError(err.response?.data?.message || 'Link inválido ou expirado.');
              } finally {
                setLoading(false);
              }
            } else if (opcao === 'PASSKEY' || opcao === 'PERMANENTE') {
              // Adicionar passo de Passkey se for o caso
              if (!steps.includes('passkey')) {
                setSteps(prev => {
                  const newSteps = [...prev];
                  newSteps.splice(currentStepIdx + 1, 0, 'passkey');
                  return newSteps;
                });
              }
              advanceOrFinish();
            } else {
              if (isMobile) {
                if (!steps.includes('passkey')) {
                  setSteps(prev => {
                    const newSteps = [...prev];
                    newSteps.splice(currentStepIdx + 1, 0, 'passkey');
                    return newSteps;
                  });
                }
                advanceOrFinish();
              } else {
                await completeLoginAndRedirect(approvalData.pendingUserData);
              }
            }
          }}
          onDenied={(msg) => {
            setError(msg || 'Acesso recusado no celular.');
            localStorage.removeItem('ac_token');
            localStorage.removeItem('ac_refresh_token');
            setTimeout(() => navigate('/login', { replace: true }), 3000);
          }}
          onBlocked={(tipo) => {
            setError(tipo === 'COMPLETO' ? 'Dispositivo bloqueado permanentemente.' : 'Acesso bloqueado.');
            localStorage.removeItem('ac_token');
            localStorage.removeItem('ac_refresh_token');
            setTimeout(() => navigate('/login', { replace: true }), 3000);
          }}
          onExpired={() => {
            setError('Tempo limite esgotado. Tente novamente.');
            localStorage.removeItem('ac_token');
            localStorage.removeItem('ac_refresh_token');
            setTimeout(() => navigate('/login', { replace: true }), 3000);
          }}
          onCancel={() => {
            localStorage.removeItem('ac_token');
            localStorage.removeItem('ac_refresh_token');
            navigate('/login', { replace: true });
          }}
        />
      ) : (
        <div style={{
          maxWidth: 480, width: '100%', animation: 'fade-in-up 0.5s ease-out',
        }}>
          {/* Shield Icon */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', margin: '0 auto 16px',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(99,102,241,0.15))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'pulse-glow 3s ease-in-out infinite',
              border: '2px solid rgba(124,58,237,0.3)',
            }}>
              <ShieldCheck size={36} color="#a78bfa" />
            </div>
            <h1 style={{ color: '#f8fafc', fontSize: '1.4rem', fontWeight: 700, margin: '0 0 6px' }}>
              Verificação de Segurança
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              {steps.length === 1
                ? 'Complete a etapa abaixo para continuar.'
                : `Complete ${steps.length} etapas de segurança para continuar.`
              }
            </p>
          </div>

          {/* Progress Bar */}
          {steps.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
              {steps.map((_, i) => (
                <div key={i} style={{
                  flex: 1, height: 4, borderRadius: 2,
                  background: i < completedCount ? '#7c3aed' : 'rgba(255,255,255,0.1)',
                  transition: 'background 0.3s ease',
                }} />
              ))}
            </div>
          )}

          {/* Step Card */}
          <div style={{
            background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: 28,
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(12px)',
          }}>
            {currentStep !== 'device_approval' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: success ? 'rgba(16,185,129,0.15)' : 'rgba(124,58,237,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${success ? 'rgba(16,185,129,0.3)' : 'rgba(124,58,237,0.3)'}`,
                }}>
                  {success
                    ? <CheckCircle2 size={22} color="#10b981" />
                    : <StepIcon size={22} color="#a78bfa" />
                  }
                </div>
                <div>
                  <div style={{ color: '#f8fafc', fontWeight: 600, fontSize: '0.95rem' }}>
                    {steps.length > 1 && <span style={{ color: '#64748b', marginRight: 6 }}>
                      {currentStepIdx + 1}/{steps.length}
                    </span>}
                    {meta.title}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{meta.description}</div>
                </div>
              </div>
            )}

            {/* Step Content */}
            {success ? (
              <div style={{
                textAlign: 'center', padding: '16px 0', color: '#10b981', fontSize: '0.9rem', fontWeight: 600,
              }}>
                <CheckCircle2 size={28} style={{ margin: '0 auto 8px', display: 'block' }} />
                Concluído! {isLast ? 'Redirecionando...' : 'Avançando...'}
              </div>
            ) : currentStep === 'device_approval' ? (
              null
            ) : currentStep === 'passkey' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {hasPasskey === null || checkingPasskey ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', color: '#94a3b8' }}>
                    <Loader2 size={18} className="animate-spin" />
                    <span style={{ fontSize: '0.9rem' }}>Verificando chaves cadastradas...</span>
                  </div>
                ) : hasPasskey ? (
                  <>
                    <button
                      onClick={handlePasskeyAuthenticate}
                      disabled={loading || passkeyLoading}
                      style={{
                        width: '100%', padding: '14px 20px', borderRadius: 12, border: 'none',
                        background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                        color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: loading ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s',
                      }}
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Fingerprint size={18} />}
                      {loading ? 'Aguardando biometria...' : 'Autenticar com Biometria'}
                    </button>
                    <p style={{ color: '#64748b', fontSize: '0.78rem', textAlign: 'center', margin: '4px 0 0' }}>
                      Toque no sensor biométrico ou use sua chave de acesso nativa.
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handlePasskeyRegister}
                      disabled={loading || passkeyLoading}
                      style={{
                        width: '100%', padding: '14px 20px', borderRadius: 12, border: 'none',
                        background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                        color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: loading ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s',
                      }}
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Fingerprint size={18} />}
                      {loading ? 'Aguardando biometria...' : 'Registrar Chave de Acesso'}
                    </button>
                    <p style={{ color: '#64748b', fontSize: '0.78rem', textAlign: 'center', margin: '4px 0 0' }}>
                      {isMobile 
                        ? 'Cadastre a biometria nativa deste celular para acessos futuros rápidos.' 
                        : 'Cadastre a biometria ou chave nativa deste computador para acessos futuros rápidos.'}
                    </p>
                  </>
                )}

                {!isMobile && (
                  <button
                    onClick={handlePasskeySkip}
                    disabled={loading}
                    style={{
                      width: '100%', padding: '10px', borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                      color: '#94a3b8', fontSize: '0.8rem', cursor: 'pointer',
                    }}
                  >
                    Pular (Dispositivo Público/Compartilhado)
                  </button>
                )}
              </div>
            ) : currentStep === 'email_verification' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {!emailSent ? (
                  <button
                    onClick={handleSendEmailCode}
                    disabled={loading}
                    style={{
                      width: '100%', padding: '14px 20px', borderRadius: 12, border: 'none',
                      background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                      color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: loading ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      opacity: loading ? 0.7 : 1,
                    }}
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                    Enviar Código para {user?.email || approvalData?.pendingUserData?.email || 'meu email'}
                  </button>
                ) : (
                  <>
                    <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0, textAlign: 'center' }}>
                      Código enviado para <strong style={{ color: '#a78bfa' }}>{user?.email || approvalData?.pendingUserData?.email}</strong>
                    </p>
                    <input
                      type="text"
                      placeholder="Digite o código"
                      value={emailCode}
                      onChange={e => setEmailCode(e.target.value)}
                      maxLength={8}
                      style={{
                        width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.06)', color: '#f8fafc', fontSize: '1.1rem',
                        textAlign: 'center', letterSpacing: '0.3em', fontWeight: 700,
                        outline: 'none',
                      }}
                      onKeyDown={e => e.key === 'Enter' && handleVerifyEmailCode()}
                      autoFocus
                    />
                    <button
                      onClick={handleVerifyEmailCode}
                      disabled={loading || !emailCode.trim()}
                      style={{
                        width: '100%', padding: '12px 20px', borderRadius: 12, border: 'none',
                        background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                        color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        opacity: loading || !emailCode.trim() ? 0.5 : 1,
                      }}
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                      Verificar
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>
                Etapa desconhecida: {currentStep}
              </p>
            )}

            {/* Error */}
            {error && (
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 10,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                color: '#fca5a5', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            {/* Skip — só em DEV (não permitido para biometria no celular/PWA) */}
            {envIsDev && !success && currentStep !== 'device_approval' && !(isMobile && currentStep === 'passkey') && (
              <button
                onClick={handleSkip}
                style={{
                  marginTop: 12, width: '100%', padding: '8px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                  color: '#64748b', fontSize: '0.72rem', cursor: 'pointer',
                }}
              >
                [DEV] Pular esta etapa
              </button>
            )}
          </div>

          {/* User info */}
          <div style={{ textAlign: 'center', marginTop: 16, color: '#475569', fontSize: '0.72rem' }}>
            Autenticado como <strong style={{ color: '#94a3b8' }}>{user?.email || approvalData?.pendingUserData?.email || '...'}</strong>
          </div>
        </div>
      )}
    </div>
  );
}
